import { useSyncExternalStore } from 'react'
import { Link } from 'react-router-dom'
import { Reveal } from '../../../components/Reveal.jsx'
import { EyebrowLabel } from '../../../components/EyebrowLabel.jsx'
import { PHOTOS } from '../constants/photos.js'

const noopSubscribe = () => () => {}

export function FeaturedService() {
  // Not in the prerendered HTML: the photo sits inside Chrome's lazy-load
  // distance on phones, so `loading="lazy"` alone still fetched it alongside
  // the hero (LCP) photo. It mounts once React hydrates; the fixed-height frame
  // keeps the layout stable.
  const hydrated = useSyncExternalStore(noopSubscribe, () => true, () => false)

  return (
    <section
      className="py-[104px] max-[640px]:py-14"
      style={{ background: 'linear-gradient(150deg,#014e4e,#008080 55%,#1f9b93)' }}
    >
      <div className="hb-wrap grid items-center gap-12 max-[640px]:gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Reveal>
          <EyebrowLabel className="!text-sprout">★ Our Most Requested Program</EyebrowLabel>
          <h2 className="mt-4 font-serif text-[clamp(32px,4.2vw,50px)] font-semibold leading-[1.08] tracking-[-0.5px] text-white">
            GLP-1 care, <em className="hl text-sprout">done properly.</em>
          </h2>
          <p className="mt-3.5 max-w-[520px] text-[17px] leading-[1.7] text-white/90 max-[640px]:mt-2.5 max-[640px]:text-[15px]">
            Compounded semaglutide and tirzepatide with real labs, monitoring, and a provider who
            adjusts your plan — not just a prescription mailed to your door.
          </p>
          <div className="mt-5 mb-6 max-[640px]:mb-5">
            <p className="font-display text-[12px] font-bold uppercase tracking-[0.14em] text-sprout">
              Simple pricing
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                {
                  interval: '2-month interval',
                  note: 'Any dose',
                  rows: [
                    { name: 'Semaglutide', price: '$37' },
                    { name: 'Tirzepatide', price: '$49' },
                  ],
                },
                {
                  interval: '3-month interval',
                  note: 'Any dose',
                  rows: [
                    { name: 'Semaglutide', price: '$34' },
                    { name: 'Tirzepatide', price: '$41' },
                  ],
                },
              ].map((card) => (
                <div
                  key={card.interval}
                  className="rounded-[18px] border border-white/20 bg-white/10 px-4 py-4 backdrop-blur-[2px]"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="!m-0 font-display text-[14px] font-extrabold !text-white">
                      {card.interval}
                    </h3>
                    <span className="rounded-full border border-sprout/40 bg-sprout/15 px-2.5 py-0.5 font-display text-[11px] font-bold text-sprout">
                      {card.note}
                    </span>
                  </div>
                  <ul className="mt-3 flex flex-col gap-2.5">
                    {card.rows.map((row) => (
                      <li
                        key={row.name}
                        className="flex items-baseline justify-between gap-3 border-t border-white/15 pt-2.5 first:border-0 first:pt-0"
                      >
                        <span className="font-sans text-[14px] font-medium text-white/95">
                          {row.name}
                        </span>
                        <span className="font-serif text-[22px] font-semibold leading-none text-white">
                          {row.price}
                          <span className="text-[13px] font-normal text-white/70">/week</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-3 font-sans text-[13px] font-light text-white/75">
              All-inclusive · labs &amp; monitoring built in
            </p>
          </div>
          <div className="mb-7 flex flex-wrap gap-2 max-[640px]:mb-5">
            {['Semaglutide', 'Tirzepatide', 'Oral & injectable', 'Provider-monitored'].map((chip) => (
              <span
                key={chip}
                className="rounded-full border-[1.5px] border-lagoon bg-white px-4 py-[7px] font-display text-[13px] font-bold text-teal-600"
              >
                {chip}
              </span>
            ))}
          </div>
          <Link to="/intake?category=weight-loss" className="btn btn-berry btn-lg">
            Check my eligibility
          </Link>
          <div className="mt-6 flex flex-wrap gap-3 max-[640px]:mt-4">
            {[
              { to: '/pricing', label: 'See full pricing' },
              { to: '/providers', label: 'Meet our providers' },
              { to: '/weight-loss', label: 'How the program works' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center rounded-full border-[1.5px] border-white/25 bg-white/10 px-[18px] py-2.5 font-display text-[13.5px] font-extrabold text-white transition hover:bg-white/20"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="h-[400px] overflow-hidden rounded-[24px] shadow-[0_24px_70px_rgba(0,90,90,0.18)] max-[1000px]:h-[280px] max-[640px]:h-[200px]">
            {hydrated && (
              <img
                src={PHOTOS.glp}
                srcSet={`${PHOTOS.glp800} 800w, ${PHOTOS.glp} 1200w`}
                sizes="(min-width: 1024px) 500px, calc(100vw - 40px)"
                alt="GLP-1 weight management program"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}
